/**
 * The LBA form as a PDF — the app's one real document, and the reason the paper
 * form is still modelled cell by cell.
 *
 * Two actions, one weight apart: **PDF herunterladen** is the amber primary and
 * **Vorschau** is a word, because they produce the same file and only one of
 * them is what the pilot came for. The preview opens the same bytes in a tab,
 * which is also where printing belongs — the browser's print of THIS page is a
 * screenshot of an app, not a document, and the card says so in its ⓘ rather
 * than offering a button that produces one.
 *
 * Under the buttons, one line about what is still open. It is a statement, not a
 * gate: the download is never disabled for it. An empty form is a legal blank
 * and a pilot who wants one is not making a mistake.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import * as api from '../../api';
import { InfoPopover } from '../../components/InfoPopover';
import { label } from '../../fields';
import { useT } from '../../i18n';
import { buildPdf, downloadPdf, pdfFilename } from '../../pdf';
import { usePlanRev, usePlanStore } from '../../planContext';
import { NAMED_LIMIT, missingIds, shortLabel } from './missing';

export function PdfCard() {
  const t = useT();
  const store = usePlanStore();
  const rev = usePlanRev();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  /** object URLs the preview handed to the browser; revoked when the pane goes */
  const urls = useRef<string[]>([]);

  const missing = useMemo(() => missingIds(id => store.val(id)), [store, rev]);
  /* `collect()` walks four hundred cells; the name it produces changes with two
     of them, so it is worth memoising against the document's own revision. */
  const filename = useMemo(() => pdfFilename(store.collect()), [store, rev]);

  useEffect(
    () => () => {
      for (const u of urls.current) URL.revokeObjectURL(u);
      urls.current = [];
    },
    [],
  );

  const run = async (preview: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const values = store.collect();
      if (!preview) {
        await downloadPdf(values);
        return;
      }
      const bytes = await buildPdf(values);
      const buffer = bytes.buffer.slice(
        bytes.byteOffset,
        bytes.byteOffset + bytes.byteLength,
      ) as ArrayBuffer;
      const url = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
      urls.current.push(url);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const named = missing.slice(0, NAMED_LIMIT).map(id => shortLabel(label(id, t))).join(', ');
  const rest = missing.length - NAMED_LIMIT;

  return (
    <section className="aex-card">
      <div className="aex-head">
        <h2 className="aex-title">{t('export.pdf.title')}</h2>
        <InfoPopover body={t('export.pdf.info')} />
      </div>
      <p className="aex-line">{t('export.pdf.line')}</p>

      <p className={'aex-state' + (missing.length === 0 ? ' aex-ok' : '')}>
        {missing.length === 0
          ? t('export.pdf.complete')
          : rest > 0
            ? t('export.pdf.missing.more', { list: named, n: rest })
            : t('export.pdf.missing', { list: named })}
      </p>

      <div className="aex-acts">
        <button
          type="button"
          className="ae-btn ae-btn-primary"
          disabled={busy}
          onClick={() => void run(false)}
        >
          {busy ? t('export.pdf.busy') : t('export.pdf.btn')}
        </button>
        <button
          type="button"
          className="ae-btn ae-btn-quiet"
          disabled={busy}
          onClick={() => void run(true)}
        >
          {t('export.pdf.preview')}
        </button>
        <span className="aex-file ae-num">{filename}</span>
      </div>

      {error ? (
        <p className="aex-err">
          {t('export.pdf.err')} {api.apiMessage(t, error)}
        </p>
      ) : null}
    </section>
  );
}
